import localForage from 'localforage';
import { useQuery, useMutation, useQueryClient } from 'react-query';

export const queryKeys = {
  all: ['devices'] as const,
  lists: () => [...queryKeys.all, 'lists'] as const,
  list: (kind: MediaDeviceKind) => [...queryKeys.lists(), { kind }] as const,
  details: () => [...queryKeys.all, 'details'] as const,
  detail: (kind: MediaDeviceKind) => [...queryKeys.details(), kind] as const,
};

export const storageKeys = {
  all: ['bonjoro', 'devices'] as const,
  audioDevice: () => [...storageKeys.all, 'audioinput'] as const,
  videoDevice: () => [...storageKeys.all, 'videoinput'] as const,
};

export async function getDevices() {
  const allDevices = await navigator.mediaDevices.enumerateDevices();

  // if there are devices with an empty deviceId, then throw since we dont have permissions
  if (allDevices.some((device) => device.deviceId.length === 0)) {
    throw new Error('No permissions');
  }

  return allDevices.filter((device) => device.kind.endsWith('input')) as (
    | InputDeviceInfo
    | MediaDeviceInfo
  )[];
}

export function useDevices() {
  return useQuery(queryKeys.lists(), getDevices);
}

export function useAudioDevices() {
  const { data: devices, isSuccess } = useDevices();

  const kind: MediaDeviceKind = 'audioinput';
  return useQuery(
    queryKeys.list(kind),
    () => devices?.filter((device) => device.kind === kind),
    {
      enabled: isSuccess,
    }
  );
}

export function useVideoDevices() {
  const { data: devices, isSuccess } = useDevices();

  const kind: MediaDeviceKind = 'videoinput';
  return useQuery(
    queryKeys.list(kind),
    () => devices?.filter((device) => device.kind === kind),
    {
      enabled: isSuccess,
    }
  );
}

const keyToStr = (key: readonly string[]) => key.join('.');

function remember<T>(key: readonly string[], value: T) {
  return localForage.setItem(keyToStr(key), JSON.stringify(value));
}

export function rememberAudioDevice(device: MediaDeviceInfo) {
  return remember(storageKeys.audioDevice(), device);
}

export function rememberVideoDevice(device: MediaDeviceInfo) {
  return remember(storageKeys.videoDevice(), device);
}

export function useRememberAudioDevice() {
  const queryClient = useQueryClient();

  return useMutation(rememberAudioDevice, {
    onSuccess: (_, device) => {
      queryClient.setQueryData(queryKeys.detail('audioinput'), device);
    },
  });
}

export function useRememberVideoDevice() {
  const queryClient = useQueryClient();

  return useMutation(rememberVideoDevice, {
    onSuccess: (_, device) => {
      queryClient.setQueryData(queryKeys.detail('videoinput'), device);
    },
  });
}

async function get<T>(key: readonly string[]): Promise<T | null> {
  const json = await localForage.getItem<string>(keyToStr(key));

  if (json) {
    return JSON.parse(json) as T;
  }

  return null;
}

export function deviceExists(
  device: MediaDeviceInfo | null | undefined,
  devices: MediaDeviceInfo[]
): device is MediaDeviceInfo {
  return device != null && devices.some((d) => d?.deviceId === device.deviceId);
}

export function getAudioDevice() {
  return get<MediaDeviceInfo>(storageKeys.audioDevice());
}

function useDevice(
  kind: MediaDeviceKind,
  getDevice: () => Promise<MediaDeviceInfo | null | undefined>
) {
  const devicesResult = useDevices();

  const { data: devices = [], isSuccess } = devicesResult;

  const result = useQuery(queryKeys.detail(kind), getDevice, {
    enabled: isSuccess && devices.length > 0,
  });

  const device = devices.find(
    (device) =>
      device.kind === kind && device.deviceId === result.data?.deviceId
  );

  return {
    ...result,
    data: device,
    isValidDevice: deviceExists(result.data, devices),
  };
}

export function useAudioDevice() {
  return useDevice('audioinput', getAudioDevice);
}

export async function getVideoDevice() {
  return get<MediaDeviceInfo>(storageKeys.videoDevice());
}

export function useVideoDevice() {
  return useDevice('videoinput', getVideoDevice);
}
